import React, { useEffect, useState } from 'react'
import { generateAnnualReport, getAnnualReport } from '../services/api';
import DateComponent from '../components/DateComponent';
import { MediumButton, SmallButton } from '../styles/Buttons';
import styled from 'styled-components';
import ComingSoon from '../components/ComingSoon';
import Spinner from '../components/Spinner';
import { Color, Colors } from '../styles/Colors';
import LoadingOverlay from '../components/LoadingOverlay';
import { FontScale } from '../styles/FontScale';

const Container = styled.div`
  padding: 1.5rem 0 1.5rem 0; 
`;

const InputWrapper = styled.div` 
& > div {
    display: flex; 
    justify-content: center; 
    align-items: center; 
    gap: 0.5rem; 
  }
`;

const ErrorMessage = styled.p`
  color: ${Color(Colors.red)}; 
  padding: 0 0 2rem 0; 
`;

const Table = styled.div` 
  display: flex; 

  & > div:first-of-type {
    flex: 4; 
  }

  & > div {
    flex: 2; 
    padding: 0 0 0 .5rem; 
    
      
    p,
    input {
      margin: 0 0 .5rem 0; 
      height: 32px; 
    }; 
  }
`;

const ListItem = styled.p` 
  padding-left: 1rem; 

  &::before {
    content: '• '; 
  }
`;

const AnnualReport = () => {
  const [annualReport, setAnnualReport] = useState(null);
  const [selectedYear, setSelectedYear] = useState(''); 
  const [inputYear, setInputYear] = useState(''); 
  const [error, setError] = useState({});
  const [isLoading, setIsLoading] = useState(''); 

  useEffect(() => {
    const savedYear = localStorage.getItem('annual-report_year')

    if (savedYear) {
      setInputYear(savedYear);
      setSelectedYear(savedYear); 
    }
  }, []); 

  const loading = {
    icon: Spinner, 
    iconFill: Color(Colors.blue),
    title: 'Var god att vänta', 
    subTitle: `Hämtar förhandsgranskning av årsrapport för ${selectedYear}`
  }; 

  const fetchAnnualReport = async () => {
    const newError = {}; 
    if (!inputYear) newError.year = 'Vänligen välj ett årtal'

    if (Object.keys(newError).length > 0) {
      setError(newError); 
      setIsLoading(false); 
      return; 
    }

    setError({}); 
    setIsLoading(true); 
    setSelectedYear(inputYear); 
    localStorage.setItem('annual-report_year', inputYear)
    
    try {
      const data = await getAnnualReport(inputYear);
      setAnnualReport(data);
    } catch (error) {
      console.log('Error fetching data', error)
    } finally {
      setIsLoading(false); 
    }
  };


  const config = [
    {
      title: `Under ${selectedYear} av SEK Svensk Elstandard fastställd svensk standard`, 
      rows: [
        { label: 'Genom översättning/återgivning', path: ['standards_publish', 'translation_reprint'] },
        { label: 'Genom återgivning', path: ['standards_publish', 'reprint'] },
        { label: 'Utan internationell eller europeisk motsvarighet', path: ['standards_publish', 'no_equivalent'] },
      ],
    },
    {
      title: 'Ändrad publiceringsform',
      rows: [
        { label: 'Ikraftsättning till återgivning ', path: ['altered_publ_form', 'reprint'] },
        { label: 'Flerspråkig ', path: ['altered_publ_form', 'translation_reprint'] },
      ],
    },
    {
      title: 'Publicerade SEK TR och SEK TS',
      hasListItems: false,
      rows: [
        { label: 'SEK Tekniska rapporter', path: ['TR_TS', 'TR_reprint'] },
        { label: 'SEK Tekniska rapporter/översättning', path: ['TR_TS', 'TR_translation_reprint'] },
        { label: 'SEK Tekniska specifikationer', path: ['TR_TS', 'TS'] }
      ],
    },
    {
      title: 'SEK Handböcker',
      rows: [
        { label: 'Reviderade', path: ['handbooks', 'revised'], isEditable: true },
        { label: 'Tidigare utgiven, nu även digitalt', path: ['handbooks', 'digital'], isEditable: true, },
        { label: 'Ny utgåva', path: ['handbooks', 'new'] },
      ],
    },
    {
      title: 'SEK Tekniklitteratur',
      rows: [
        { label: 'Ny Tekniklitteratur', path: ['tekniklitteratur'] },
      ],
    },
    {
      title: 'Antal gällande',
      hasListItems: false,
      rows: [
        { label: 'Standarder', path: ['active_standards'] },
        { label: 'SEK Tekniska rapporter', path: ['active_TR'] },
        { label: 'SEK Tekniska specifikationer', path: ['active_TS'] },
        { label: 'SEK CWA', path: ['active_CWA'] },
        { label: 'SEK Handböcker', path: ['active_handbooks'] },
        { label: 'SEK Tekniklitteratur', path: ['active_tekniklitteratur'] },
      ],
    },
    {
      title: '\u00A0',
      hasListItems: false,
      isMissingTitle: true,
      rows: [
        { label: `Upphävda standarder under ${selectedYear}`, path: ['annulled'] },
      ],
    },
  ];

  const getValue = (obj, path) => {
    return path.reduce((current, key) => current?.[key], obj)
  };

  const calculatedValues = (section) => {
    return section.rows.reduce(
      (sum, row) => {
        const val = getValue(annualReport, row.path) || {};
        return {
          count: sum.count + (Number(val.count) || 0),
          pages: sum.pages + (Number(val.pages) || 0)
        };
      },
      { count: 0, pages: 0 }
    );
  }; 

  const setValue = (path, field, value) => {
    setAnnualReport(prev => {
      const updated = structuredClone(prev);
      let current = updated; 

      path.forEach((key, index) => {
        if (index === path.length - 1) {
          current[key] = {
            ...(current[key] || {}), 
            [field]: value === '' ? '' : Number(value), 
          }
        } else {
          current[key] = current[key] || {}
          current = current[key]
        }
      })
      return updated
    })
  }; 

  const handleGenerateAnnualReport = async () => {
    try {
      await generateAnnualReport(selectedYear, annualReport);
    } catch (error) {
      console.log(`Failed to generate annual report`)
    }; 
  }; 

  return (
    <Container>
      {isLoading ? (
        <>
        {<LoadingOverlay {...loading} />}
        </>
      ) : (
        <>
          <h2>{`Fastställelse och utgivning ${selectedYear ?? ''}`}</h2>
          <InputWrapper>
            <div>
              <input
                type='number'
                min='1900'
                max='2900'
                value={inputYear}
                onChange={(event) => setInputYear(event.target.value)}
                placeholder='Välj årtal'
              />
              <SmallButton onClick={fetchAnnualReport}>Hämta årsrapport</SmallButton>
            </div>
            {Object.keys(error).length > 0 && (
              <ErrorMessage>{error.year}</ErrorMessage>
            )}
          </InputWrapper>
    
          {annualReport && (
            <>
              <div>
    
                {config.map((section, index) => {
                  const total = calculatedValues(section);
                  return (
                    <div key={section.title}>
                      <Table style={{padding: index === 5 ? 0 : '0 0 1.5rem 0' }}>
                        <div>
                          <h5 style={{padding: index === config.length -1 ? 0 : '0 0 1rem 0' }}>{section.title}</h5>
                          
                          {section.rows.map(row => (
                            <React.Fragment key={row.label}>
                              {section.hasListItems === false
                                ? <p 
                                  style={{ 
                                    fontWeight: section.isMissingTitle ? 600 : 400, 
                                    fontSize: section.isMissingTitle ? FontScale.size_2 : FontScale.size_1
                                  }} 
                                >
                                  {row.label}
                                </p>
                                : <ListItem key={row.label}>{row.label}</ListItem>
                              }
                            </React.Fragment>
                          ))}
                          {index === 0 ? <h5>Antal nya standarder</h5> : ''}
                        </div>
                        <div>
                          {index === 0 ? <h5 style={{padding: '0 0 1rem 0' }}>Antal</h5> : <h5 style={{padding: index === config.length -1 ? 0 : '0 0 1rem 0' }}>&nbsp;</h5>}
                          {section.rows.map(row => {
                            const val = getValue(annualReport, row.path)
                            return (
                              <div key={row.label}>
                                {row.isEditable === true ? (
                                  <input 
                                    type='number' 
                                    min='0'
                                    value={getValue(annualReport, row.path)?.count ?? ''}
                                    onChange={(event) => setValue(row.path, 'count', event.target.value)}
                                    placeholder='Välj antal exemplar'
                                  />
                                ) : (
                                  <p style={{fontWeight: index === config.length -1 ? 600 : 400 }}>{val?.count ? val.count : '-'}</p>
                                )}
                              </div>
                            )
                          })}
                          {index === 0 ? <p style={{ fontWeight: 600 }}>{total.count}</p> : ''}
                        </div>
                        <div>
                          {index === 0 ? <h5 style={{padding: '0 0 1rem 0' }}>Sidor</h5> : <h5 style={{padding: index === config.length -1 ? 0 : '0 0 1rem 0' }}>&nbsp;</h5>}
                          {section.rows.map(row => {
                            const val = getValue(annualReport, row.path)
                            return (
                              <div key={row.label}>
                                {row.isEditable === true ? (
                                  <input 
                                    type='number' 
                                    min='0'
                                    value={getValue(annualReport, row.path)?.pages ?? ''}
                                    onChange={(event) => setValue(row.path, 'pages', event.target.value)}
                                    placeholder='Välj antal sidor'
                                  />
                                ) : (
                                  <p style={{fontWeight: index === config.length -1 ? 600 : 400 }}>{val?.pages ? val.pages : '-'}</p>
                                )}
                              </div>
                            )
                          })}
                          {index === 0 ? <p  style={{ fontWeight: 600 }}>{total.pages}</p> : ''}
                        </div>
                      </Table>
                    </div>
                  )
                })}
                <MediumButton onClick={handleGenerateAnnualReport}>Genrera årsrapport</MediumButton>
              </div>
            </>
          )}
        </>
      )}
    </Container>
  )
}

export default AnnualReport